import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';

import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import {
	CardStatusMessage,
	DlkStatusMessage,
	DriverInfoMessage,
	SOCKET_REQUEST_TYPE, SOCKET_RESPONSE_TYPE,
	SocketMessage
} from '@core/driver-card.model';
import { TranslateService } from '@ngx-translate/core';
import { SEGMENT } from '@root/app-routing.model';
import { AppConfig } from '@root/app.config';
import { ALERT_MESSAGE, AlertService } from '@root/modules/core/alert/alert.service';
import { SocketService } from '@root/modules/core/services/socket.service';

marker('tachomat.choose-card.error.card-is-not-present');
marker('tachomat.choose-card.error.could-not-read-card');

@Injectable({
	providedIn: 'root'
})
export class DriverCardService implements OnDestroy, OnInit {
	private onDestroy$: Subject<null> = new Subject();
	private initialConnection: boolean = true;

	private socket?: Subject<SocketMessage>;
	private _cardPulledOut$: Subject<void> = new Subject();
	private _cardExisting: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	private _driverInfo: BehaviorSubject<DriverInfoMessage | null> = new BehaviorSubject<DriverInfoMessage | null>(null);
	private _dlkStatus$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	private _title$: BehaviorSubject<string> = new BehaviorSubject('');
	private _subtitle$: BehaviorSubject<string> = new BehaviorSubject('');

	/** Used in template to change current page title */
	title$: Observable<string> = this._title$.asObservable();

	subtitle$: Observable<string> = this._subtitle$.asObservable();

	get cardPulledOut$(): Observable<void> {
		return this._cardPulledOut$.asObservable();
	}

	get cardExisting$(): Observable<boolean> {
		return this._cardExisting.asObservable();
	}

	get cardExisting(): boolean {
		return this._cardExisting.getValue();
	}

	get driverInfo$(): Observable<DriverInfoMessage | null> {
		return this._driverInfo.asObservable();
	}

	get driverInfo(): DriverInfoMessage | null {
		return this._driverInfo.getValue();
	}

	get dlkStatus$(): Observable<boolean> {
		return this._dlkStatus$.asObservable();
	}

	get dlkStatus(): boolean {
		return this._dlkStatus$.getValue();
	}


	constructor(
		private readonly config: AppConfig,
		private readonly socketService: SocketService,
		private readonly alertService: AlertService,
		private readonly translate: TranslateService,
		private readonly router: Router,
	) { }

	ngOnInit() {
		this._title$.subscribe(value => console.log('Title:', value));
		this._subtitle$.subscribe(value => console.log('Subtitle:', value));
	}
	ngOnDestroy() {
		this.onDestroy$.next();
	}

	public setTitle(msgKey: string, parameters?: object): void {
		setTimeout(() => {
			this._title$.next(this.translate.instant(msgKey, parameters));
		});
	}
	public setSubtitle(msgKey: string, parameters?: object): void {
		setTimeout(() => {
			this._subtitle$.next(this.translate.instant(msgKey, parameters));
		});
	}

	private processCardStatus(cardData: CardStatusMessage, initialConnection: boolean = false): void {
		if (!initialConnection && typeof cardData.cardPresent === 'boolean') {
			if (cardData.cardPresent) {
				this.alertService.addMessage(
					ALERT_MESSAGE.INFO,
					marker(
						'tachomat.choose-card.insert-card.error-card-pull-in'
					),
					true
				);
				this.router.navigate([SEGMENT.CONNECT]);
			} else {
				this.alertService.addMessage(
					ALERT_MESSAGE.DANGER,
					marker(
						'tachomat.choose-card.insert-card.error-card-pull-out'
					),
					true
				);
				this._cardPulledOut$.next();
			}
		}
		this._cardExisting.next(cardData.cardPresent);
		this.initialConnection = false;
	}

	public connectToSocket(): void {
		this.socket = <Subject<SocketMessage>>(
			this.socketService
				.connect(this.config.getConfig('tachomat.ws.hostname'))
				.pipe(
					map((resp: MessageEvent): SocketMessage => JSON.parse(resp.data))
				)
		);

		this.socket
			.pipe(takeUntil(this.onDestroy$))
			.subscribe(
				(resp: SocketMessage) => {
					switch (resp.type) {
						case SOCKET_RESPONSE_TYPE.CARD: {
							this._driverInfo.next(null);
							this.processCardStatus(resp as CardStatusMessage, this.initialConnection);
							break;
						}

						case SOCKET_RESPONSE_TYPE.DRIVER_INFO: {
							this._driverInfo.next(resp as DriverInfoMessage);
							break;
						}

						case SOCKET_RESPONSE_TYPE.ERROR: {
							this._driverInfo.next(null);
							this.processCardStatus(resp as CardStatusMessage, this.initialConnection);
							this.alertService.addMessage(
								ALERT_MESSAGE.DANGER,
								`tachomat.choose-card.error.${resp.errorCode}`
							);
							break;
						}

						case SOCKET_RESPONSE_TYPE.DLK_STATUS: {
							this._dlkStatus$.next((resp as DlkStatusMessage).dlkConnected);
							break;
						}

						default:
							break;
					}
				},
				(error: ErrorEvent) => {
					this._driverInfo.next(null);
					this.alertService.addMessage(
						ALERT_MESSAGE.DANGER,
						marker(
							'tachomat.choose-card.error.socket-connection'
						)
					);
				}
			);
	}

	askForCard(): Observable<boolean> {
		if (this.socket) {
			this.socket.next({ type: SOCKET_REQUEST_TYPE.CARD_STATUS });
		}
		return this._cardExisting.asObservable();
	}

	askForDriverInfo(): Observable<DriverInfoMessage | null> {
		if (this.socket) {
			this.socket.next({ type: SOCKET_REQUEST_TYPE.DRIVER_INFO });
		}
		return this._driverInfo.asObservable();
	}

	askForDlkStatus(): Observable<boolean> {
		if (this.socket) {
			this.socket.next({ type: SOCKET_REQUEST_TYPE.DLK_STATUS });
		}
		return this._dlkStatus$.asObservable();
	}

	getLanguagesList(): Observable<string[]> {
		return of(['hr', 'fr', 'de', 'es', 'en']);
	}
}
